/**
 * Periodic-table-inspired product image placeholders for missing or broken images.
 */

export type ProductPlaceholderTheme = 'light' | 'dark';

export interface ProductPlaceholderInput {
  name: string;
  sku?: string | null;
  category?: string | null;
  theme?: ProductPlaceholderTheme;
  /** Square SVG viewport size (default 400). */
  size?: number;
}

export interface ProductPlaceholderParts {
  abbreviation: string;
  displayName: string;
  categoryLabel: string;
  atomicNumber: number;
}

const PEPTIDE_CODE_PATTERN = /\b([A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+)\b/i;

const PEPTIDE_NAME_PATTERNS: ReadonlyArray<{ pattern: RegExp; code: string }> = [
  { pattern: /\bBPC-157\b/i, code: 'BPC-157' },
  { pattern: /\bMOTS-C\b/i, code: 'MOTS-C' },
  { pattern: /\bARA-290\b/i, code: 'ARA-290' },
  { pattern: /\bAOD-9604\b/i, code: 'AOD-9604' },
  { pattern: /\b5-Amino[\s-]*1MQ\b/i, code: '5-Amino-1MQ' },
  { pattern: /\b9-ME-BC\b/i, code: '9-ME-BC' },
  { pattern: /\bTB-?500\b/i, code: 'TB-500' },
  { pattern: /\bGHK-Cu\b/i, code: 'GHK-Cu' },
  { pattern: /\bSemaglutide\b/i, code: 'SEMA' },
  { pattern: /\bTirzepatide\b/i, code: 'TIRZ' },
];

const THEME_PALETTES: Record<
  ProductPlaceholderTheme,
  {
    bgStart: string;
    bgEnd: string;
    border: string;
    accent: string;
    text: string;
    muted: string;
    grid: string;
  }
> = {
  light: {
    bgStart: '#fafaf9',
    bgEnd: '#e7e5e4',
    border: '#b4932a',
    accent: '#b4932a',
    text: '#1c1917',
    muted: '#78716c',
    grid: 'rgba(180,147,42,0.08)',
  },
  dark: {
    bgStart: '#1e293b',
    bgEnd: '#0f172a',
    border: '#22c55e',
    accent: '#22c55e',
    text: '#f8fafc',
    muted: '#94a3b8',
    grid: 'rgba(34,197,94,0.1)',
  },
};

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function truncate(text: string, maxLength: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

function normalizeAbbreviation(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '-');
}

function extractHyphenatedPrefix(text: string, maxSegments = 2): string | null {
  const tokens = text.split(/[-_\s/]+/).filter(Boolean);
  if (tokens.length < 2) {
    return null;
  }

  const parts = tokens.slice(0, maxSegments);
  if (parts.every((part) => /^[A-Z0-9]+$/i.test(part))) {
    return normalizeAbbreviation(parts.join('-'));
  }

  return null;
}

function extractPeptideCodeFromText(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  for (const { pattern, code } of PEPTIDE_NAME_PATTERNS) {
    if (pattern.test(trimmed)) {
      return normalizeAbbreviation(code);
    }
  }

  const codeMatch = trimmed.match(PEPTIDE_CODE_PATTERN);
  if (codeMatch?.[1]) {
    return normalizeAbbreviation(codeMatch[1]);
  }

  return null;
}

function extractAbbreviationFromSku(sku: string): string | null {
  const trimmed = sku.trim();
  if (!trimmed) {
    return null;
  }

  const fromPattern = extractPeptideCodeFromText(trimmed);
  if (fromPattern) {
    return fromPattern;
  }

  const fromPrefix = extractHyphenatedPrefix(trimmed, 2);
  if (fromPrefix) {
    return fromPrefix;
  }

  const tokens = trimmed.split(/[-_\s/]+/).filter(Boolean);
  for (const token of tokens) {
    if (/^[A-Z0-9]{2,8}$/i.test(token) && !/^\d+$/.test(token)) {
      return normalizeAbbreviation(token);
    }
  }

  return null;
}

function extractAbbreviationFromName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    const initials = words
      .map((word) => word.replace(/[^A-Za-z0-9]/g, ''))
      .filter(Boolean)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 4);

    if (initials.length >= 2) {
      return initials;
    }
  }

  const firstToken = words[0] ?? '';
  if (/^[A-Z0-9]*-[A-Z0-9]+/i.test(firstToken) || /\d/.test(firstToken)) {
    return normalizeAbbreviation(firstToken);
  }

  return null;
}

/**
 * Derive a periodic-table-style abbreviation from product name and/or SKU.
 */
export function extractProductAbbreviation(name: string, sku?: string | null): string {
  const trimmedName = name.trim();
  const fromNamePattern = extractPeptideCodeFromText(trimmedName);
  if (fromNamePattern) {
    return fromNamePattern;
  }

  const trimmedSku = (sku ?? '').trim();
  const fromSku = trimmedSku ? extractAbbreviationFromSku(trimmedSku) : null;
  if (fromSku) {
    return fromSku;
  }

  const fromName = extractAbbreviationFromName(trimmedName);
  if (fromName) {
    return fromName;
  }

  return 'PT';
}

function hashToAtomicNumber(seed: string): number {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(index);
    hash |= 0;
  }
  return (Math.abs(hash) % 118) + 1;
}

function resolveCategoryLabel(category?: string | null): string {
  const trimmed = (category ?? '').trim();
  if (!trimmed) {
    return 'Peptide';
  }
  return truncate(trimmed, 14);
}

function abbreviationFontSize(abbreviation: string, size: number): number {
  const length = abbreviation.length;
  if (length <= 4) {
    return size * 0.22;
  }
  if (length <= 7) {
    return size * 0.17;
  }
  if (length <= 10) {
    return size * 0.13;
  }
  return size * 0.1;
}

/**
 * Structured parts used to render the placeholder tile.
 */
export function deriveProductPlaceholderParts(input: ProductPlaceholderInput): ProductPlaceholderParts {
  const name = (input.name ?? '').trim() || 'Product';
  const abbreviation = extractProductAbbreviation(name, input.sku);
  const categoryLabel = resolveCategoryLabel(input.category);

  return {
    abbreviation,
    displayName: truncate(name, 28),
    categoryLabel,
    atomicNumber: hashToAtomicNumber(`${abbreviation}:${name}:${input.sku ?? ''}`),
  };
}

/**
 * Build an inline SVG string for a periodic-table-inspired product tile.
 */
export function buildProductPlaceholderSvg(input: ProductPlaceholderInput): string {
  const size = input.size ?? 400;
  const theme = input.theme ?? 'light';
  const palette = THEME_PALETTES[theme];
  const parts = deriveProductPlaceholderParts(input);
  const abbrevSize = abbreviationFontSize(parts.abbreviation, size);
  const pad = size * 0.08;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="${escapeXml(parts.displayName)}">`,
    `<defs>`,
    `<linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">`,
    `<stop offset="0%" stop-color="${palette.bgStart}"/>`,
    `<stop offset="100%" stop-color="${palette.bgEnd}"/>`,
    `</linearGradient>`,
    `<pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">`,
    `<path d="M24 0H0V24" fill="none" stroke="${palette.grid}" stroke-width="1"/>`,
    `</pattern>`,
    `</defs>`,
    `<rect width="${size}" height="${size}" rx="${size * 0.04}" fill="url(#bg)"/>`,
    `<rect x="1.5" y="1.5" width="${size - 3}" height="${size - 3}" rx="${size * 0.035}" fill="none" stroke="${palette.border}" stroke-width="2.5"/>`,
    `<rect width="${size}" height="${size}" fill="url(#grid)" opacity="0.65"/>`,
    `<text x="${pad}" y="${pad + abbrevSize * 0.35}" fill="${palette.muted}" font-family="ui-monospace,Consolas,monospace" font-size="${size * 0.055}" font-weight="600">${parts.atomicNumber}</text>`,
    `<text x="${size - pad}" y="${pad + abbrevSize * 0.35}" fill="${palette.muted}" font-family="Inter,Segoe UI,sans-serif" font-size="${size * 0.045}" text-anchor="end" letter-spacing="0.08em">${escapeXml(parts.categoryLabel.toUpperCase())}</text>`,
    `<text x="${size / 2}" y="${size * 0.52}" fill="${palette.text}" font-family="ui-monospace,Consolas,monospace" font-size="${abbrevSize}" font-weight="700" text-anchor="middle" dominant-baseline="middle" letter-spacing="0.04em">${escapeXml(parts.abbreviation)}</text>`,
    `<text x="${size / 2}" y="${size - pad}" fill="${palette.accent}" font-family="Inter,Segoe UI,sans-serif" font-size="${size * 0.055}" text-anchor="middle" letter-spacing="0.06em">${escapeXml(parts.displayName.toUpperCase())}</text>`,
    `</svg>`,
  ].join('');
}

/**
 * Data URI suitable for img[src] placeholders.
 */
export function buildProductPlaceholderDataUri(input: ProductPlaceholderInput): string {
  const svg = buildProductPlaceholderSvg(input);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
